/**
 * Ordre du classement saison / division :
 * 1. points championnat (demi + finale)
 * 2. points qualification (si championnat ex æquo, typiquement tout à 0 en qualifs)
 * 3. points de course (monnaie)
 * 4. nom puis prénom
 */

export interface SeasonStandingSortKey {
  points: number;
  qualPoints?: number;
  coursePoints?: number;
  driverLastName?: string;
  driverFirstName?: string;
  /** Variante getSeasonStandings (nom affiché unique). */
  driverName?: string;
}

export function compareSeasonStandings(a: SeasonStandingSortKey, b: SeasonStandingSortKey): number {
  if (a.points !== b.points) return b.points - a.points;
  const qualA = a.qualPoints ?? 0;
  const qualB = b.qualPoints ?? 0;
  if (qualA !== qualB) return qualB - qualA;
  const courseA = a.coursePoints ?? 0;
  const courseB = b.coursePoints ?? 0;
  if (courseA !== courseB) return courseB - courseA;
  const last = (a.driverLastName ?? '').localeCompare(b.driverLastName ?? '', 'fr');
  if (last !== 0) return last;
  const first = (a.driverFirstName ?? '').localeCompare(b.driverFirstName ?? '', 'fr');
  if (first !== 0) return first;
  return (a.driverName ?? '').localeCompare(b.driverName ?? '', 'fr');
}

export function sortSeasonStandings<T extends SeasonStandingSortKey>(rows: readonly T[]): T[] {
  return [...rows].sort(compareSeasonStandings);
}

/** Pilote visible au classement (champ, qualif ou course). */
export function seasonStandingHasScore(row: SeasonStandingSortKey): boolean {
  return row.points > 0 || (row.qualPoints ?? 0) > 0 || (row.coursePoints ?? 0) > 0;
}

/**
 * Score affiché : championnat dès qu’il y en a, sinon points de qualification
 * (phase qualifs, tout le monde à 0 en champ).
 */
export function seasonStandingDisplayPoints(row: SeasonStandingSortKey): number {
  if (row.points > 0) return row.points;
  return row.qualPoints ?? 0;
}

/** Leader division / teaser : 1er après tri champ → qualif → course. */
export function pickSeasonStandingLeader<T extends SeasonStandingSortKey>(
  standings: readonly T[]
): T | null {
  const eligible = standings.filter(seasonStandingHasScore);
  if (eligible.length === 0) return null;
  return sortSeasonStandings(eligible)[0] ?? null;
}
